<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      execution?: 'render' | 'execute';
      appearance?: 'always' | 'execute' | 'interaction-only';
    }
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
  execute: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const props = withDefaults(
  defineProps<{
    siteKey: string;
    action?: string;
    theme?: 'light' | 'dark' | 'auto';
    execution?: 'render' | 'execute';
    appearance?: 'always' | 'execute' | 'interaction-only';
  }>(),
  {
    action: undefined,
    theme: 'light',
    execution: 'render',
    appearance: 'always',
  }
);

const emit = defineEmits<{
  (event: 'verified', token: string): void;
  (event: 'expired'): void;
  (event: 'error'): void;
}>();

const SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_STATUS_ATTRIBUTE = 'data-turnstile-status';

let turnstileScriptPromise: Promise<void> | null = null;

const turnstileContainer = ref<HTMLElement | null>(null);
const widgetId = ref<string | null>(null);
const currentToken = ref<string | null>(null);
const widgetClass = computed(() => ({
  'turnstile-widget--always': props.appearance === 'always',
}));

let pendingExecutionPromise: Promise<string> | null = null;
let pendingExecutionResolve: ((token: string) => void) | null = null;
let pendingExecutionReject: ((error: Error) => void) | null = null;

const isTurnstileReady = (): boolean => Boolean(window.turnstile);

const clearPendingExecution = (): void => {
  pendingExecutionPromise = null;
  pendingExecutionResolve = null;
  pendingExecutionReject = null;
};

const resolvePendingExecution = (token: string): void => {
  if (pendingExecutionResolve) {
    pendingExecutionResolve(token);
  }
  clearPendingExecution();
};

const rejectPendingExecutionWith = (message: string): void => {
  if (pendingExecutionReject) {
    pendingExecutionReject(new Error(message));
  }
  clearPendingExecution();
};

const clearWidgetState = (): void => {
  currentToken.value = null;
  clearPendingExecution();
};

const setScriptStatus = (script: HTMLScriptElement, status: 'loading' | 'loaded' | 'error'): void => {
  script.setAttribute(SCRIPT_STATUS_ATTRIBUTE, status);
};

const loadTurnstileScript = async (): Promise<void> => {
  if (isTurnstileReady()) {
    return;
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise<void>((resolve, reject) => {
    const finalizeResolve = () => {
      turnstileScriptPromise = Promise.resolve();
      resolve();
    };

    const finalizeReject = (script: HTMLScriptElement) => {
      setScriptStatus(script, 'error');
      script.remove();
      turnstileScriptPromise = null;
      reject(new Error('TURNSTILE-SCRIPT-LOAD-FAILED'));
    };

    const attachListeners = (script: HTMLScriptElement) => {
      script.addEventListener('load', () => {
        setScriptStatus(script, 'loaded');
        finalizeResolve();
      }, { once: true });
      script.addEventListener('error', () => finalizeReject(script), { once: true });
    };

    const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      const existingStatus = existingScript.getAttribute(SCRIPT_STATUS_ATTRIBUTE);

      if (isTurnstileReady() || existingStatus === 'loaded') {
        finalizeResolve();
        return;
      }

      if (existingStatus === 'error') {
        existingScript.remove();
      } else {
        attachListeners(existingScript);
        return;
      }
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    setScriptStatus(script, 'loading');
    attachListeners(script);

    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

const renderWidget = (): void => {
  if (!props.siteKey || !turnstileContainer.value || !window.turnstile) {
    return;
  }

  if (widgetId.value) {
    rejectPendingExecutionWith('TURNSTILE-WIDGET-RENDERED');
    window.turnstile.remove(widgetId.value);
    widgetId.value = null;
  }

  currentToken.value = null;
  widgetId.value = window.turnstile.render(turnstileContainer.value, {
    sitekey: props.siteKey,
    action: props.action,
    theme: props.theme,
    execution: props.execution,
    appearance: props.appearance,
    callback: (token: string) => {
      currentToken.value = token;
      emit('verified', token);
      resolvePendingExecution(token);
    },
    'expired-callback': () => {
      currentToken.value = null;
      emit('expired');
      rejectPendingExecutionWith('TURNSTILE-TOKEN-EXPIRED');
    },
    'error-callback': () => {
      currentToken.value = null;
      emit('error');
      rejectPendingExecutionWith('TURNSTILE-EXECUTION-FAILED');
    },
  });
};

const ensureWidgetReady = async (): Promise<void> => {
  if (widgetId.value && window.turnstile) {
    return;
  }

  await loadTurnstileScript();
  renderWidget();

  if (!widgetId.value || !window.turnstile) {
    throw new Error('TURNSTILE-WIDGET-UNAVAILABLE');
  }
};

const executeVerification = async (): Promise<string> => {
  if (currentToken.value) {
    return currentToken.value;
  }

  if (pendingExecutionPromise) {
    return pendingExecutionPromise;
  }

  await ensureWidgetReady();

  pendingExecutionPromise = new Promise<string>((resolve, reject) => {
    pendingExecutionResolve = resolve;
    pendingExecutionReject = reject;
  });

  try {
    window.turnstile?.execute(widgetId.value || undefined);
  } catch {
    rejectPendingExecutionWith('TURNSTILE-EXECUTION-FAILED');
  }

  if (!pendingExecutionPromise) {
    throw new Error('TURNSTILE-EXECUTION-FAILED');
  }

  return pendingExecutionPromise;
};

const reset = (): void => {
  currentToken.value = null;
  rejectPendingExecutionWith('TURNSTILE-RESET');

  if (!widgetId.value || !window.turnstile) {
    return;
  }
  window.turnstile.reset(widgetId.value);
};

onMounted(async () => {
  try {
    await loadTurnstileScript();
    renderWidget();
  } catch {
    emit('error');
  }
});

watch(
  () => [props.siteKey, props.action, props.theme, props.execution, props.appearance],
  () => {
    renderWidget();
  }
);

onBeforeUnmount(() => {
  rejectPendingExecutionWith('TURNSTILE-WIDGET-UNMOUNTED');
  if (widgetId.value && window.turnstile) {
    window.turnstile.remove(widgetId.value);
  }
  clearWidgetState();
});

defineExpose({
  executeVerification,
  reset,
});
</script>

<template>
  <div ref="turnstileContainer" :class="['turnstile-widget', widgetClass]" />
</template>

<style scoped>
.turnstile-widget--always {
  min-height: 65px;
}
</style>
